import { type PresenceStatus, SEARCH_MIN_QUERY_LENGTH } from "@bing/shared";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { FriendView } from "../api/client";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { GameInviteSheet } from "../components/GameInviteSheet";
import { ScreenTitle } from "../components/ScreenTitle";
import { displayName } from "../components/profile-ui";
import {
  EmptyState,
  IconButton,
  IdentityButton,
  LoadError,
  Pill,
  Row,
  SectionHeader,
  SocialIcon,
  Spinner,
} from "../components/social-ui";
import { faDigits } from "../i18n/digits";
import { faApiError } from "../i18n/errors";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";

/* ------------------------------------------------------------------ *
 * Friends tab — three sub-tabs over the FriendView-based friends API:
 *   «دوستان»    — accepted friends (invite-to-game + remove).
 *   «درخواست‌ها» — incoming (accept/reject) + outgoing (cancel), with a count badge.
 *   «افزودن»     — user search → the SAME per-state actions as the lists.
 * Rows carry ONLY friend-state actions (the follow feature was removed from the
 * UI by user decision; its api endpoints stay dormant). Row actions mutate ONLY
 * from the server's returned FriendView (no optimistic flips); failures surface
 * the REAL Persian reason via the error-code map (`faApiError`).
 * ------------------------------------------------------------------ */

type Tab = "friends" | "requests" | "add";

/** The full per-row mutation surface a panel wires into its rows. */
interface RowActions {
  request: (u: FriendView) => void;
  accept: (u: FriendView) => void;
  reject: (u: FriendView) => void;
  cancel: (u: FriendView) => void;
  remove: (u: FriendView) => void;
  unblock: (u: FriendView) => void;
  invite: (u: FriendView) => void;
}

/**
 * The CORRECT trailing actions for a row, derived from the relationship state
 * the server returned (never from which list the row happens to sit in):
 *   none → افزودن دوست · outgoing pending → لغو درخواست · incoming pending →
 *   پذیرش/رد · accepted → دعوت به بازی + حذف · blocked → آنبلاک.
 */
function StateActions({
  u,
  busy,
  on,
}: {
  u: FriendView;
  busy: boolean;
  on: RowActions;
}): ReactNode {
  if (u.status === "accepted") {
    return (
      <>
        <Pill
          variant="primary"
          icon="invite"
          label={fa.friends.invite}
          disabled={busy}
          onClick={() => on.invite(u)}
        />
        <Pill
          variant="outline"
          icon="remove"
          label={fa.friends.remove}
          disabled={busy}
          onClick={() => on.remove(u)}
        />
      </>
    );
  }
  if (u.status === "blocked") {
    return (
      <Pill
        variant="outline"
        label={fa.friends.unblock}
        disabled={busy}
        onClick={() => on.unblock(u)}
      />
    );
  }
  if (u.status === "pending") {
    return u.direction === "incoming" ? (
      <>
        <IconButton
          tone="accept"
          icon="check"
          label={fa.friends.accept}
          disabled={busy}
          onClick={() => on.accept(u)}
        />
        <IconButton
          tone="reject"
          icon="close"
          label={fa.friends.reject}
          disabled={busy}
          onClick={() => on.reject(u)}
        />
      </>
    ) : (
      <Pill
        variant="outline"
        icon="cancel"
        label={fa.friends.cancel}
        disabled={busy}
        onClick={() => on.cancel(u)}
      />
    );
  }
  return (
    <Pill
      variant="primary"
      icon="personAdd"
      label={fa.friends.add}
      disabled={busy}
      onClick={() => on.request(u)}
    />
  );
}

/** One social row: presence-ringed avatar + identity + friend-state actions.
 * `showHandle` renders the `@handle` subline — the Add/search tab only. */
function UserRow({
  u,
  presence,
  busy,
  on,
  muted = false,
  size = 48,
  showHandle = false,
}: {
  u: FriendView;
  presence?: PresenceStatus;
  busy: boolean;
  on: RowActions;
  muted?: boolean;
  size?: number;
  showHandle?: boolean;
}): ReactNode {
  const openUserProfile = useSession((s) => s.openUserProfile);
  return (
    <Row>
      {/* The whole identity area opens the profile — see `IdentityButton`, which «کاربران
          مسدود» shares so both screens have one tap target, not two. */}
      <IdentityButton
        user={u}
        onOpen={() => openUserProfile(u.userId)}
        presence={presence}
        muted={muted}
        size={size}
        showHandle={showHandle}
      />
      {/* The trailing actions are SIBLINGS of the profile button, so a tap on
          «افزودن دوست»/«پذیرش»/«حذف»… can never bubble into it. The explicit
          stopPropagation is belt-and-braces: it keeps that guarantee even if this
          row ever grows a click handler of its own. */}
      <div className="flex shrink-0 items-center gap-xs" onClick={(e) => e.stopPropagation()}>
        <StateActions u={u} busy={busy} on={on} />
      </div>
    </Row>
  );
}

export function Friends(): ReactNode {
  const friends = useSession((s) => s.friends);
  const incoming = useSession((s) => s.incoming);
  const outgoing = useSession((s) => s.outgoing);
  const presence = useSession((s) => s.presence);
  const incomingCount = useSession((s) => s.incomingCount);
  const friendsLoading = useSession((s) => s.friendsLoading);
  const friendsError = useSession((s) => s.friendsError);
  const refreshFriends = useSession((s) => s.refreshFriends);
  const requestFriend = useSession((s) => s.requestFriend);
  const acceptFriend = useSession((s) => s.acceptFriend);
  const rejectFriend = useSession((s) => s.rejectFriend);
  const cancelFriend = useSession((s) => s.cancelFriend);
  const removeFriend = useSession((s) => s.removeFriend);
  const unblockUser = useSession((s) => s.unblockUser);
  const inviteFriendToGame = useSession((s) => s.inviteFriendToGame);
  const selfId = useSession((s) => s.profile?.id ?? null);
  const api = useSession((s) => s.api);

  const [tab, setTab] = useState<Tab>("friends");
  const [inviteTarget, setInviteTarget] = useState<FriendView | null>(null);
  // «حذف» is guarded by a confirm dialog — the pending target, or null.
  const [removeTarget, setRemoveTarget] = useState<FriendView | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FriendView[]>([]);
  const [searchState, setSearchState] = useState<"idle" | "searching" | "done">("idle");
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  // Focus refresh: the tab mounts only while it's the active view, so mount = focus.
  useEffect(() => {
    void refreshFriends();
  }, [refreshFriends]);

  // User search (≥2 chars), with stale-response cancellation. The caller's own
  // row is excluded — every action on it would be a SELF_ACTION error anyway.
  useEffect(() => {
    const q = query.trim();
    if (q.length < SEARCH_MIN_QUERY_LENGTH) {
      setResults([]);
      setSearchState("idle");
      return;
    }
    let cancelled = false;
    setSearchState("searching");
    void api
      .searchUsers(q)
      .then((hits) => {
        if (!cancelled) {
          setResults(hits.filter((h) => h.userId !== selfId));
          setSearchState("done");
        }
      })
      .catch(() => {
        if (!cancelled) setSearchState("done");
      });
    return () => {
      cancelled = true;
    };
  }, [api, query, selfId]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);
  const showToast = (msg: string): void => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  const setBusyFor = (userId: string, on: boolean): void =>
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(userId);
      else next.delete(userId);
      return next;
    });

  /** Run a mutation with per-row busy state + the REAL Persian reason on failure.
   * The row's state never flips on error — state changes only come from the
   * server-returned view the store (or `patchResult`) applies. */
  const withBusy = async (userId: string, fn: () => Promise<unknown>): Promise<void> => {
    setBusyFor(userId, true);
    try {
      await fn();
    } catch (error) {
      showToast(faApiError(error));
    } finally {
      setBusyFor(userId, false);
    }
  };

  const patchResult = (view: FriendView): void =>
    setResults((rs) => rs.map((r) => (r.userId === view.userId ? view : r)));

  /** The store mutations, each also patching the local search row (a no-op for
   * rows living in the store lists). */
  const mutate = (u: FriendView, fn: (id: string) => Promise<FriendView>): Promise<void> =>
    withBusy(u.userId, async () => patchResult(await fn(u.userId)));

  const on: RowActions = {
    request: (u) => void mutate(u, requestFriend),
    accept: (u) => void mutate(u, acceptFriend),
    reject: (u) => void mutate(u, rejectFriend),
    cancel: (u) => void mutate(u, cancelFriend),
    // «حذف» only opens the confirm dialog — removal happens exclusively from
    // its confirm button (never on the row tap itself).
    remove: (u) => setRemoveTarget(u),
    unblock: (u) => void mutate(u, unblockUser),
    // «دعوت به بازی» opens the game sheet; picking a game hosts the private
    // table and the waiting room auto-sends the invite (see GameInviteSheet).
    invite: (u) => setInviteTarget(u),
  };

  const TABS: readonly { key: Tab; label: string }[] = [
    { key: "friends", label: fa.friends.tabFriends },
    { key: "requests", label: fa.friends.tabRequests },
    { key: "add", label: fa.friends.tabAdd },
  ];

  return (
    <div
      className="flex min-h-full flex-col bg-background text-on-surface"
      style={{
        backgroundImage:
          "radial-gradient(circle at top, rgb(var(--c-room-light) / 0.8) 0%, rgb(var(--c-surface)) 80%)",
      }}
      dir="rtl"
    >
      <header
        className="sticky top-0 z-20 flex h-14 items-center justify-center border-b border-outline-variant/30 bg-surface/90 px-md"
        style={{ backdropFilter: "blur(12px)" }}
      >
        <ScreenTitle icon="friends" title={fa.friends.title} />
      </header>

      <main className="mx-auto flex w-full max-w-container-max flex-col gap-md px-md py-md">
        {/* Sub-tabs */}
        <div className="flex gap-1 rounded-xl bg-surface-container-highest p-1">
          {TABS.map((tb) => {
            const on2 = tab === tb.key;
            const badge = tb.key === "requests" && incomingCount > 0;
            return (
              <button
                key={tb.key}
                type="button"
                aria-label={tb.label}
                aria-pressed={on2}
                onClick={() => setTab(tb.key)}
                className={`flex flex-1 appearance-none items-center justify-center gap-1.5 rounded-lg border-0 py-2 font-body-md text-body-md transition-colors ${
                  on2
                    ? "bg-gold-deep text-ink-on-gold shadow"
                    : "cursor-pointer bg-transparent text-on-surface-variant hover:text-on-surface"
                }`}
              >
                <span>{tb.label}</span>
                {badge ? (
                  <span
                    className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-error px-1 font-label-md text-label-md text-on-error"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {faDigits(incomingCount)}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {tab === "friends" ? (
          <FriendsPanel
            friends={friends}
            presence={presence}
            loading={friendsLoading}
            error={friendsError}
            busy={busy}
            on={on}
            onRetry={() => void refreshFriends()}
          />
        ) : tab === "requests" ? (
          <RequestsPanel
            incoming={incoming}
            outgoing={outgoing}
            loading={friendsLoading}
            error={friendsError}
            busy={busy}
            on={on}
            onRetry={() => void refreshFriends()}
          />
        ) : (
          <AddPanel
            query={query}
            onQuery={setQuery}
            results={results}
            searchState={searchState}
            busy={busy}
            on={on}
          />
        )}
      </main>

      {toast ? (
        <div
          role="alert"
          className="fixed inset-x-0 bottom-24 z-50 mx-auto w-max max-w-[90%] rounded-full bg-error px-4 py-2 font-body-md text-body-md text-on-error shadow-lg"
        >
          {toast}
        </div>
      ) : null}

      {inviteTarget ? (
        <GameInviteSheet
          friend={inviteTarget}
          onClose={() => setInviteTarget(null)}
          onPick={(game) => {
            inviteFriendToGame(inviteTarget.userId, game);
            setInviteTarget(null);
          }}
        />
      ) : null}

      {/* Unfriend confirmation — the shared ConfirmDialog (same as the exit
          flows); the API call fires ONLY from its confirm button. */}
      {removeTarget ? (
        <ConfirmDialog
          title={fa.friends.removeConfirmTitle}
          body={fa.friends.removeConfirmBody(displayName(removeTarget))}
          confirmLabel={fa.friends.removeConfirm}
          cancelLabel={fa.friends.removeCancel}
          onConfirm={() => {
            setRemoveTarget(null);
            void mutate(removeTarget, removeFriend);
          }}
          onCancel={() => setRemoveTarget(null)}
        />
      ) : null}
    </div>
  );
}

/* ── Friends (accepted) ── */
function FriendsPanel({
  friends,
  presence,
  loading,
  error,
  busy,
  on,
  onRetry,
}: {
  friends: FriendView[];
  presence: Record<string, PresenceStatus>;
  loading: boolean;
  error: string | null;
  busy: Set<string>;
  on: RowActions;
  onRetry: () => void;
}): ReactNode {
  return (
    <section className="flex flex-col gap-sm">
      <SectionHeader
        icon="group"
        title={fa.friends.friendsTitle}
        count={friends.length > 0 ? friends.length : undefined}
      />
      {loading && friends.length === 0 ? (
        <Spinner />
      ) : error && friends.length === 0 ? (
        <LoadError onRetry={onRetry} />
      ) : friends.length === 0 ? (
        <EmptyState icon="group" text={fa.friends.noFriends} hint={fa.friends.noFriendsHint} />
      ) : (
        friends.map((u) => (
          <UserRow
            key={u.userId}
            u={u}
            presence={presence[u.userId]}
            busy={busy.has(u.userId)}
            on={on}
          />
        ))
      )}
    </section>
  );
}

/* ── Requests (incoming + outgoing) ── */
function RequestsPanel({
  incoming,
  outgoing,
  loading,
  error,
  busy,
  on,
  onRetry,
}: {
  incoming: FriendView[];
  outgoing: FriendView[];
  loading: boolean;
  error: string | null;
  busy: Set<string>;
  on: RowActions;
  onRetry: () => void;
}): ReactNode {
  if (loading && incoming.length === 0 && outgoing.length === 0) return <Spinner />;
  if (error && incoming.length === 0 && outgoing.length === 0)
    return <LoadError onRetry={onRetry} />;
  return (
    <div className="flex flex-col gap-md">
      <section className="flex flex-col gap-sm">
        <SectionHeader
          icon="personAdd"
          title={fa.friends.incomingTitle}
          count={incoming.length > 0 ? incoming.length : undefined}
        />
        {incoming.length === 0 ? (
          <EmptyState icon="personAdd" text={fa.friends.noIncoming} />
        ) : (
          incoming.map((u) => <UserRow key={u.userId} u={u} busy={busy.has(u.userId)} on={on} />)
        )}
      </section>

      <section className="flex flex-col gap-sm">
        <SectionHeader
          icon="cancel"
          title={fa.friends.outgoingTitle}
          count={outgoing.length > 0 ? outgoing.length : undefined}
        />
        {outgoing.length === 0 ? (
          <EmptyState icon="cancel" text={fa.friends.noOutgoing} />
        ) : (
          outgoing.map((u) => (
            <UserRow key={u.userId} u={u} busy={busy.has(u.userId)} on={on} muted size={44} />
          ))
        )}
      </section>
    </div>
  );
}

/* ── Add (user search) ── */
function AddPanel({
  query,
  onQuery,
  results,
  searchState,
  busy,
  on,
}: {
  query: string;
  onQuery: (q: string) => void;
  results: FriendView[];
  searchState: "idle" | "searching" | "done";
  busy: Set<string>;
  on: RowActions;
}): ReactNode {
  const partial = query.trim().length > 0 && query.trim().length < SEARCH_MIN_QUERY_LENGTH;

  return (
    <section className="flex flex-col gap-sm">
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-on-surface-variant">
          <SocialIcon name="search" className="h-5 w-5" />
        </span>
        <input
          type="text"
          dir="rtl"
          aria-label={fa.friends.searchLabel}
          placeholder={fa.friends.searchPlaceholder}
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          className="w-full appearance-none rounded-xl border border-outline-variant bg-surface-container-high py-sm pl-3 pr-10 text-right font-body-md text-body-md text-on-surface shadow-inner outline-none transition-colors placeholder:text-on-surface-variant focus:border-secondary focus:ring-1 focus:ring-secondary"
        />
      </div>

      {partial ? (
        <p className="m-0 px-sm font-caption text-caption text-on-surface-variant">
          {fa.friends.searchHint}
        </p>
      ) : searchState === "searching" ? (
        <Spinner />
      ) : results.length > 0 ? (
        // Search hits keep the `@handle` subline — this is the one surface
        // where users look people up BY handle, so it is informative here.
        results.map((u) => (
          <UserRow key={u.userId} u={u} busy={busy.has(u.userId)} on={on} size={44} showHandle />
        ))
      ) : searchState === "done" ? (
        <EmptyState icon="search" text={fa.friends.noResults} />
      ) : null}
    </section>
  );
}
